import React, { useEffect, useState, useRef } from "react"

// libraries
import Skeleton from "react-loading-skeleton"
import { Button } from "@mui/material"

// helpers
import Api from "@/_helpers/Api"

// providers
import { trans } from "@/_providers/Translation"
import { useCalculateColor } from "@/_helpers/useCalculateColor"

import CompactFilterLayout from "@/components/partials/CompactFilterLayout"
import DateRange from "@/components/partials/DateRange"
import filterImage from "@/assets/img/icons/filter.svg"

const INITIAL_FILTERS = { date_from: null, date_to: null }

function getCategoriesFromApi(apiChartData) {
  if (!apiChartData) return []
  const labels = Array.isArray(apiChartData.labels) ? apiChartData.labels : []
  const values = Array.isArray(apiChartData.values) ? apiChartData.values : []
  const percentages = Array.isArray(apiChartData.percentages)
    ? apiChartData.percentages
    : []
  return labels.map((label, idx) => ({
    label,
    value: values[idx] || 0,
    percentage: percentages[idx] || 0,
  }))
}

function ClientsByCategories() {
  const [loading, setLoading] = useState(true)
  const [apiData, setApiData] = useState(null)
  const [filters, setFilters] = useState(INITIAL_FILTERS)

  const compactFilterLayoutRef = useRef(null)
  const fetchDebounceRef = useRef(null)

  const { getColorVariation } = useCalculateColor()

  useEffect(() => {
    clearTimeout(fetchDebounceRef.current)
    fetchDebounceRef.current = setTimeout(() => {
      loadData()
    }, 300)
    return () => clearTimeout(fetchDebounceRef.current)
  }, [filters])

  function loadData() {
    setLoading(true)
    const f = `${filters.date_from ? `?date_from=${filters.date_from || ""}` : ""}${filters.date_to ? `&date_to=${filters.date_to || ""}` : ""}`
    Api.get(`analytics/clients-by-category${f}`)
      .then((res) => {
        if (res.data) setApiData(res.data)
        setLoading(false)
      })
      .catch(() => setLoading(false))
  }

  function handleFilterChange(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }))
  }

  function getAppliedFiltersCount() {
    let count = 0
    if (filters.date_from || filters.date_to) count++
    return count
  }

  return (
    <div className="clients-by-category-chart">
      <div className="head">
        <h3>{trans("analytics.clientsByCategory")}</h3>
        <div className="right">
          <Button
            onClick={(e) => {
              compactFilterLayoutRef.current.open(e)
            }}
          >
            <img src={filterImage} alt="Filter" />
            {getAppliedFiltersCount() ? (
              <i>{getAppliedFiltersCount()}</i>
            ) : (
              <></>
            )}
          </Button>
          <CompactFilterLayout
            ref={compactFilterLayoutRef}
            appliedFiltersCount={getAppliedFiltersCount()}
            onClearClick={() => setFilters(INITIAL_FILTERS)}
          >
            <div className="row">
              <div className="col">
                <DateRange
                  startDate={filters.date_from || ""}
                  endDate={filters.date_to || ""}
                  onStartDateChange={(date) =>
                    handleFilterChange("date_from", date)
                  }
                  onEndDateChange={(date) =>
                    handleFilterChange("date_to", date)
                  }
                />
              </div>
            </div>
          </CompactFilterLayout>
        </div>
      </div>
      <hr />
      {loading ? (
        <Skeleton height={300} count={1} />
      ) : (
        <div className="cbc-list-wrapper">
          {getCategoriesFromApi(apiData?.chart_data).map((cat, idx, arr) => {
            let i = idx

            if (idx >= 3) {
              i = 3
            }

            return (
              <div className="cbc-row" key={cat.label}>
                <div className="cbc-row-head">
                  <span className="cbc-label">{cat.label}</span>
                  <span className="cbc-value">{cat.value}</span>
                </div>
                <div className="cbc-bar-bg">
                  <div
                    className="cbc-bar"
                    style={{
                      background: `linear-gradient(
                        to right,
                        ${getColorVariation(i)},
                        ${getColorVariation(i + 4)})`,
                      width: `${(cat.value / Math.max(...arr.map((c) => c.value), 1)) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

export default ClientsByCategories
